import { Box } from "@mui/material";
import clsx from "clsx";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { ReactNode, useEffect, useRef, useState } from "react";

import { useBrushOnCanvas } from "../../../hooks/brush/useBrushOnCanvas.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { BrushMode, IBrushSettings } from "../../../models/brush.ts";
import { KeyPointIcon } from "../../../pages/Labs/VideoEditing/Config/Eraser/Popup/Config/KeyPoints/icons/KeyPoint.tsx";
import { RemoveKeyPointIcon } from "../../../pages/Labs/VideoEditing/Config/Eraser/Popup/Config/KeyPoints/icons/RemoveKeyPointIcon.tsx";
import { SegmentationMaskTypes } from "../../../pages/Playground/ImageToImage/Config/Features/EraseObject/eraseObjectConstants.ts";
import LoadingAnimation from "../LoadingAnimation/LoadingAnimation.tsx";
import styles from "./BriaCanvas.module.scss";

export type MarkerPoint = {
	id: string;
	x: number;
	y: number;
};

export type BriaImageProps = {
	url: string;
	enableBrush?: boolean;
	brushConfigs: IBrushSettings;
	isLoading?: boolean;
	className?: string;
	pointProps?: PointProps;

	canvasRef?: (ref: HTMLCanvasElement | null) => void;
	canvasOverlayRef?: (ref: HTMLCanvasElement | null) => void;
	onBeforeCanvasSizeUpdate?: () => void;
	onCanvasSizeUpdate?: () => void;
};

type CanvasScale = {
	offsetX: number;
	offsetY: number;
	scaleX: number;
	scaleY: number;
};

export type PointProps = {
	points: MarkerPoint[];
	onPointsChange: (points: MarkerPoint[]) => void;
	addMarker?: ReactNode;
	removeMarker?: ReactNode;
};

type PointMarkerLayerProps = {
	points: MarkerPoint[];
	canvasLayout: CanvasScale;
	brushMode: BrushMode;
	onPointsChange: (points: MarkerPoint[]) => void;
	addMarker?: ReactNode;
	removeMarker?: ReactNode;
};

const PointMarkerLayer = ({
	points,
	canvasLayout,
	brushMode,
	onPointsChange,
	addMarker,
	removeMarker,
}: PointMarkerLayerProps) => {
	const handlePointClick = (e: React.MouseEvent, pointId: string) => {
		e.stopPropagation(); // Prevent triggering canvas click

		if (brushMode === "point-erase") {
			onPointsChange(points.filter((p) => p.id !== pointId));
		}
	};

	return (
		<>
			{points.map((point) => {
				const displayX = point.x * canvasLayout.scaleX + canvasLayout.offsetX;
				const displayY = point.y * canvasLayout.scaleY + canvasLayout.offsetY;

				return (
					<Box
						key={point.id}
						className={clsx(styles.pointMarker, {
							[styles.removePoint]: brushMode === "point-erase",
						})}
						style={{
							left: displayX,
							top: displayY,
						}}
						onClick={(e) => handlePointClick(e, point.id)}
					>
						{brushMode === "point" && (addMarker ?? <KeyPointIcon />)}
						{brushMode === "point-erase" && (removeMarker ?? <RemoveKeyPointIcon />)}
					</Box>
				);
			})}
		</>
	);
};

const BriaCanvas = ({
	className,
	url,
	enableBrush = true,
	canvasRef,
	canvasOverlayRef,
	isLoading = false,
	brushConfigs,
	onBeforeCanvasSizeUpdate,
	onCanvasSizeUpdate,
	pointProps,
}: BriaImageProps) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const loadingContainerRef = useRef<HTMLDivElement | null>(null);
	const localCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const localCanvasOverlayRef = useRef<HTMLCanvasElement | null>(null);
	const cursorRef = useRef<HTMLDivElement | null>(null);
	const originalImageDimensionsRef = useRef<{ width: number; height: number } | null>(null);
	const { imageToImageStore } = useAppStore();
	const { editingCanvasStore } = imageToImageStore; // TODO: Remove the usage of this store and make a prop for this feature

	// Canvas sizing info for positioning point markers on resize
	const [canvasSizing, setCanvasSizing] = useState<CanvasScale | null>(null);
	const [isImageReady, setIsImageReady] = useState(false);

	const { clearBrushOverlayCanvasRef, captureMask, restoreMask } = useBrushOnCanvas(
		brushConfigs,
		localCanvasRef,
		localCanvasOverlayRef,
		cursorRef,
	);

	const isPointMode = ["point", "point-erase"].includes(brushConfigs.brushMode) && pointProps;

	// Determine if brush should be active (disabled when point mode is on)
	const isBrushActive = enableBrush && !isPointMode;

	useEffect(() => {
		if (brushConfigs.reset) {
			clearBrushOverlayCanvasRef();
			runInAction(() => {
				brushConfigs.reset = false;
			});

			pointProps?.onPointsChange?.([]);
		}
	}, [brushConfigs.reset]);

	useEffect(() => {
		if (!enableBrush) return;
		if (localCanvasRef.current && localCanvasOverlayRef.current) {
			canvasRef?.(localCanvasRef.current);
			canvasOverlayRef?.(localCanvasOverlayRef.current);
		}

		const canvas = localCanvasRef.current;
		const context = canvas?.getContext("2d");
		const container = containerRef.current;

		if (!canvas || !context || !container) {
			return;
		}

		setIsImageReady(false);

		const updateCanvasSize = () => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.src = url;

			// TODO: Split this function into multiple private functions
			img.onload = () => {
				// Store original image dimensions for point coordinate calculations
				originalImageDimensionsRef.current = { width: img.width, height: img.height };

				const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();

				const imgAspectRatio = img.width / img.height;
				let drawWidth = img.width;
				let drawHeight = img.height;

				if (drawWidth > containerWidth) {
					drawWidth = containerWidth;
					drawHeight = drawWidth / imgAspectRatio;
				}

				if (containerHeight > 0 && drawHeight > containerHeight) {
					drawHeight = containerHeight;
					drawWidth = drawHeight * imgAspectRatio;
				}

				// Capture overlay content before resizing clears it
				captureMask();
				onBeforeCanvasSizeUpdate?.();

				canvas.width = drawWidth;
				canvas.height = drawHeight;
				if (localCanvasOverlayRef && localCanvasOverlayRef.current) {
					localCanvasOverlayRef.current.width = drawWidth;
					localCanvasOverlayRef.current.height = drawHeight;
				}

				if (loadingContainerRef && loadingContainerRef.current) {
					loadingContainerRef.current.style.width = `${drawWidth}px`;
					loadingContainerRef.current.style.height = `${drawHeight}px`;
				}
				context.clearRect(0, 0, canvas.width, canvas.height);
				context.drawImage(img, 0, 0, drawWidth, drawHeight);
				setIsImageReady(true);

				// Restore captured mask at new canvas size
				restoreMask();
				onCanvasSizeUpdate?.();

				requestAnimationFrame(() => {
					// Update canvas layout info for point rendering (after DOM settles)
					if (localCanvasRef.current && containerRef.current && originalImageDimensionsRef.current) {
						const canvasRect = localCanvasRef.current.getBoundingClientRect();
						const containerRect = containerRef.current.getBoundingClientRect();
						const layout = {
							offsetX: canvasRect.left - containerRect.left,
							offsetY: canvasRect.top - containerRect.top,
							scaleX: canvasRect.width / originalImageDimensionsRef.current.width,
							scaleY: canvasRect.height / originalImageDimensionsRef.current.height,
						};

						setCanvasSizing(layout);
					}
				});
			};
		};

		updateCanvasSize();
		window.addEventListener("resize", updateCanvasSize);

		return () => {
			window.removeEventListener("resize", updateCanvasSize);
			canvasRef?.(null);
		};
	}, [url]);

	useEffect(() => {
		/* 
			Set the "draw-logic" (composite operation).
			"destination-out" erases drawn pixels.
			"source-over" draws on top of the image.
		*/
		if (["eraser", "brush"].includes(brushConfigs.brushMode)) {
			runInAction(() => {
				brushConfigs.globalCompositeOperation =
					brushConfigs.brushMode === "eraser" ? "destination-out" : "source-over";
			});
		}
	}, [brushConfigs.brushMode]);

	useEffect(() => {
		if (localCanvasOverlayRef.current) {
			canvasOverlayRef?.(localCanvasOverlayRef.current);
		}
	}, [localCanvasOverlayRef]);

	const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
		// Only allow adding new points in "point" mode
		if (
			!pointProps ||
			brushConfigs.brushMode !== "point" ||
			!localCanvasRef.current ||
			!originalImageDimensionsRef.current
		)
			return;

		const { points, onPointsChange } = pointProps;
		const canvas = localCanvasRef.current;
		const rect = canvas.getBoundingClientRect();

		// Calculate click position relative to the canvas
		const canvasX = e.clientX - rect.left;
		const canvasY = e.clientY - rect.top;

		// Check if click is within canvas bounds
		if (canvasX < 0 || canvasX > rect.width || canvasY < 0 || canvasY > rect.height) return;

		// Convert canvas coordinates to original image coordinates
		const scaleX = originalImageDimensionsRef.current.width / rect.width;
		const scaleY = originalImageDimensionsRef.current.height / rect.height;
		const x = canvasX * scaleX;
		const y = canvasY * scaleY;

		const nextId = points.length > 0 ? Math.max(...points.map((p) => parseInt(p.id))) + 1 : 0;
		const newPoint: MarkerPoint = {
			id: String(nextId),
			x,
			y,
		};

		onPointsChange?.([...points, newPoint]);
	};

	return (
		<Box
			className={clsx(styles.container, className, {
				[styles.pointCreationMode]: isPointMode,
			})}
			ref={containerRef}
			onClick={handleCanvasClick}
		>
			{isBrushActive && (
				<Box
					ref={cursorRef}
					className={clsx(styles.customCursor, {
						[styles.eraserCursor]: brushConfigs.brushMode === "eraser",
					})}
					style={{
						width: brushConfigs.lineWidth,
						height: brushConfigs.lineWidth,

						// Dynamic values: eraser needs borderColor, brush needs backgroundColor + opacity
						...(brushConfigs.brushMode === "eraser"
							? { borderColor: brushConfigs.strokeStyle }
							: { backgroundColor: brushConfigs.strokeStyle, opacity: brushConfigs.brushColorOpacity }),
					}}
				></Box>
			)}
			<canvas
				onContextMenu={(e) => e.preventDefault()}
				className={clsx(styles.briaCanvas, {
					[styles.isLoading]: isLoading,
					[styles.hidden]: !isImageReady,
				})}
				ref={localCanvasRef}
			/>

			<canvas
				className={clsx({
					[styles.isLoading]: isLoading,
					[styles.hidden]: !isImageReady,
					[styles.automaticSegmentCanvas]:
						editingCanvasStore.objectManipulationConfig?.mask_type === SegmentationMaskTypes.AUTOMATIC,
					[styles.pointerEventsNone]: isPointMode,
				})}
				onContextMenu={(e) => e.preventDefault()}
				ref={localCanvasOverlayRef}
			/>

			{/* Render point markers via memoized sub-component */}
			{isPointMode && canvasSizing && (
				<PointMarkerLayer
					points={pointProps.points}
					canvasLayout={canvasSizing}
					brushMode={brushConfigs.brushMode}
					onPointsChange={pointProps.onPointsChange}
					addMarker={pointProps.addMarker}
					removeMarker={pointProps.removeMarker}
				/>
			)}

			<Box className={styles.loadingContainer} ref={loadingContainerRef}>
				<Box>
					<LoadingAnimation loading={isLoading} progressBarTime={10} />
				</Box>
			</Box>
		</Box>
	);
};

const ObservedComponent = observer(BriaCanvas);
export default ObservedComponent;
